import styled from '@emotion/styled';

import { hoverable } from '../../interaction-states';
import { forHover } from '../../responsive';
import { spacing } from '../../spacing';

export type CardSize = 'md' | 'lg';
export type CardVariant = 'outline' | 'filled';
export type CardMarkRole = 'radio' | 'checkbox';

const SIZES: Record<CardSize, { paddingBlock: number; paddingInline: number; gap: number }> = {
  md: { paddingBlock: spacing[12], paddingInline: spacing[16], gap: spacing[8] },
  lg: { paddingBlock: spacing[16], paddingInline: spacing[20], gap: spacing[12] },
};

export const StyledCardRoot = styled('label', {
  shouldForwardProp: (prop) => !['$size', '$variant', '$mark'].includes(prop as string),
})<{ $size: CardSize; $variant: CardVariant; $mark: CardMarkRole }>(({
  theme,
  $size,
  $variant,
  $mark,
}) => {
  const { paddingBlock, paddingInline, gap } = SIZES[$size];
  const isFilled = $variant === 'filled';
  const mark = `[role="${$mark}"]`;
  const enabled = `:not(:has(${mark}[data-disabled]))`;
  const cardHover = hoverable(theme, isFilled ? 'surfaceRaised' : 'border', `&${enabled}`);

  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap,
    boxSizing: 'border-box',
    minWidth: 0,
    // The design system draws the 1px border inside the padded frame.
    padding: `${paddingBlock - 1}px ${paddingInline - 1}px`,
    borderRadius: theme.radius[12],
    border: `1px solid ${isFilled ? 'transparent' : theme.colors.border.default}`,
    backgroundColor: isFilled ? theme.colors.neutral.solid : 'transparent',
    cursor: 'pointer',

    transition: cardHover.transition,

    [forHover]: {
      ...cardHover[forHover],
      [`&${enabled}:hover ${mark}:not([data-checked])`]: {
        borderColor: theme.colors.border.strong,
      },
      [`&${enabled}:hover [role="radio"][data-checked]`]: {
        '--radio-dot-color': theme.colors.brand.hover,
      },
    },

    '&:has(:focus-visible)': {
      outline: `2px solid ${theme.colors.border.focus}`,
      outlineOffset: -2,
    },

    [`&:has(${mark}[data-disabled])`]: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

export const StyledCardMarkSlot = styled('span')<{ $size: CardSize }>(({ $size }) => ({
  display: 'flex',
  flexShrink: 0,
  paddingTop: $size === 'md' ? spacing[2] : 0,
}));

export const StyledCardCopy = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing[2],
  flex: 1,
  minWidth: 0,
  wordBreak: 'break-word',
});
